// Airport landing pages (Google Ads and search), rendered by landing/LandingPage.js.
// Each airport has its own copy, prices and FAQ; prices come from prices.json via landing/prices.js.
// Claims must match the Terms (60 min free waiting after landing, free changes up to 1 hour
// before pickup) and the Help center (amenities, stops, tips).
// Titles and descriptions also go in pageMeta.json so the prerender writes them into the HTML.
import { BOOK_POINT, EXTRAS, INCLUDED } from './landing/common';
import { cheapest, money, route, rows } from './landing/prices';

const jfk = route('jfk-manhattan');
const lga = route('lga-manhattan');
const ewr = route('ewr-manhattan');

const fromOf = (list) => list.length && {
  sedan: Math.min(...list.map((r) => r.sedan)),
  suv: Math.min(...list.map((r) => r.suv)),
};

const common = (code) => ({
  book: BOOK_POINT,
  airport: code ? code.toLowerCase() : null, // hero buttons pre-fill the airport in /book
  included: INCLUDED.airport,
  extras: EXTRAS.airport,
  schema: {
    name: code ? `${code} airport car service` : 'NYC airport car service',
    serviceType: 'Airport transfer',
    areaServed: ['Manhattan', 'Brooklyn', 'Queens', ...(code ? [] : ['JFK Airport', 'LaGuardia Airport', 'Newark Airport'])],
  },
});

const jfkRows = rows([
  ['jfk-manhattan', 'JFK → Midtown Manhattan'],
  ['jfk-manhattan', 'JFK → Downtown Manhattan'],
  ['jfk-brooklyn', 'JFK → Downtown Brooklyn'],
  ['jfk-queens', 'JFK → Long Island City, Queens'],
  ['manhattan-jfk', 'Anywhere in Manhattan → JFK'],
]).map((r, i) => ({ ...r, key: `${r.key}-${i}` }));

const lgaRows = rows([
  ['lga-manhattan', 'LaGuardia → Midtown Manhattan'],
  ['lga-manhattan', 'LaGuardia → Downtown Manhattan'],
  ['lga-brooklyn', 'LaGuardia → Downtown Brooklyn'],
  ['lga-queens', 'LaGuardia → Long Island City, Queens'],
  ['manhattan-lga', 'Anywhere in Manhattan → LaGuardia'],
]).map((r, i) => ({ ...r, key: `${r.key}-${i}` }));

const ewrRows = rows([
  ['ewr-manhattan', 'Newark → Midtown Manhattan'],
  ['ewr-manhattan', 'Newark → Downtown Manhattan'],
  ['ewr-brooklyn', 'Newark → Downtown Brooklyn'],
  ['ewr-queens', 'Newark → Long Island City, Queens'],
  ['ewr-jersey-city', 'Newark → Jersey City'],
  ['ewr-hoboken', 'Newark → Hoboken'],
  ['manhattan-ewr', 'Anywhere in Manhattan → Newark'],
]).map((r, i) => ({ ...r, key: `${r.key}-${i}` }));

const allRows = rows([
  ['jfk-manhattan', 'JFK ↔ Manhattan'],
  ['lga-manhattan', 'LaGuardia ↔ Manhattan'],
  ['ewr-manhattan', 'Newark → Manhattan'],
  ['manhattan-ewr', 'Manhattan → Newark'],
  ['jfk-brooklyn', 'JFK → Downtown Brooklyn'],
  ['lga-brooklyn', 'LaGuardia → Downtown Brooklyn'],
  ['ewr-brooklyn', 'Newark → Downtown Brooklyn'],
]);

const PAGES = {
  all: allRows.length && jfk && lga && ewr && {
    ...common(null),
    path: '/airport-transfers',
    code: null,
    linkText: 'All NYC airport transfers',
    eyebrow: 'Airport car service · New York',
    title: <>NYC airport car service, <em>priced upfront</em></>,
    lead: 'Private chauffeurs to and from JFK, LaGuardia and Newark. Flat rates to Manhattan, flight tracking and 60 minutes of free waiting after you land.',
    heroImage: 'airport-terminal',
    from: fromOf(allRows),
    cta: 'Book your airport ride',
    introEyebrow: 'Three airports, one service',
    introTitle: <>Which airport, <em>which ride</em></>,
    intro: [
      'New York has three major airports, and each one is a different trip. LaGuardia, in northern Queens, is the closest to Manhattan and handles mostly domestic flights. JFK, in southeast Queens, is the main international gateway and the longest ride to Midtown at rush hour. Newark Liberty is across the Hudson in New Jersey, and often the quickest airport for the West Side and Lower Manhattan.',
      `Whichever you use, RoolDrive works the same way. Between any of the three airports and an address in Manhattan, the price is a flat rate: ${money(lga.sedan)} from LaGuardia, ${money(jfk.sedan)} from JFK and ${money(ewr.sedan)} from Newark in a Sedan. Rides to Brooklyn, Queens, the Bronx, Staten Island, Long Island, Westchester or New Jersey are priced by distance. Either way, the total is on screen before you pay.`,
      'Add your flight number when you book and your chauffeur follows your flight. If it lands early or late, they adjust, and waiting is free for the first 60 minutes after landing.',
    ],
    facts: [
      ['Airports', 'JFK, LaGuardia, Newark'],
      ['To Manhattan', 'Flat rate'],
      ['Free waiting', '60 min after landing'],
      ['Book', 'Online, app or phone, 24/7'],
    ],
    priceTitle: <>Airport <em>prices</em></>,
    priceLead: 'Flat rates between each airport and Manhattan, and typical prices to Brooklyn. Choose your airport for more destinations.',
    priceTables: [{ caption: 'NYC airport car service prices', rows: allRows, firstColumn: 'Route' }],
    priceNote: 'Manhattan routes are flat rates. Brooklyn prices are for Downtown Brooklyn and depend on distance.',
    sections: [
      {
        eyebrow: 'How airport pickups work',
        title: <>From the gate <em>to the car</em></>,
        paragraphs: [
          'When you book, enter the airport as your pickup, your address as the drop-off, and your flight number. Your chauffeur is assigned in advance and you can see their name, vehicle and plate. When you land, you get a notification when your chauffeur has arrived, and you can call or message them directly to meet.',
          'For departures, we pick you up at home or the office and drop you at your terminal. Give yourself enough time for check-in and security, especially for international flights from JFK and Newark.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a RoolDrive car at night',
      },
    ],
    faqTitle: 'Airport transfer questions',
    faq: [
      { q: 'Which NYC airport is closest to Manhattan?', a: 'LaGuardia, about 9 miles from Midtown. Newark is about 16 miles and JFK about 17, but traffic matters more than distance: allow extra time at rush hour for all three.' },
      { q: 'Are airport prices fixed?', a: `Yes, between each airport and Manhattan: in a Sedan, ${money(lga.sedan)} for LaGuardia, ${money(jfk.sedan)} for JFK and ${money(ewr.sedan)} from Newark. Other destinations are priced by distance, also shown before you pay.` },
      { q: 'What if my flight is delayed?', a: 'Add your flight number when you book. Your chauffeur tracks the flight and adjusts to delays, and waiting is free for the first 60 minutes after you land.' },
      { q: 'Can you take us between two airports?', a: 'Yes, for example JFK to Newark for a connection. Enter both airports and the ride is priced by distance before you book.' },
      { q: 'How many bags fit?', a: 'A Sedan takes up to 3 passengers and 3 large bags; an SUV up to 6 passengers and 6 large bags.' },
      { q: 'Can I change or cancel?', a: 'Yes, free of charge up to 1 hour before pickup, on the website or in the app. Card payments are refunded in full when you cancel in time.' },
    ].filter(Boolean),
    related: ['/jfk-car-service', '/laguardia-car-service', '/newark-airport-car-service', '/flat-rate-car-service'],
  },

  jfk: jfkRows.length && jfk && {
    ...common('JFK'),
    path: '/jfk-car-service',
    code: 'JFK',
    linkText: 'JFK car service',
    eyebrow: 'JFK car service',
    title: <>JFK car service, <em>{money(jfk.sedan)} to Manhattan</em></>,
    lead: `Private chauffeur between JFK and anywhere in Manhattan for a flat ${money(jfk.sedan)} in a Sedan. Flight tracked, 60 minutes of free waiting, and your total shown before you pay.`,
    heroImage: 'airport-terminal',
    from: fromOf(jfkRows),
    cta: 'Book your JFK ride',
    introEyebrow: 'John F. Kennedy International',
    introTitle: <>New York’s <em>international gateway</em></>,
    intro: [
      'JFK is in southeast Queens, about 17 miles from Midtown Manhattan. Most rides into the city take the Van Wyck Expressway and then the Grand Central Parkway or Long Island Expressway, crossing into Manhattan by the RFK Bridge or the Queens-Midtown Tunnel. Brooklyn is usually reached on the Belt Parkway.',
      'Without heavy traffic, the ride to Midtown takes 40 to 60 minutes. On weekday afternoons and Friday evenings it can take 75 to 90 minutes. That is why a flat rate matters here: from JFK to any Manhattan address, the price is the same whatever the traffic.',
      'International arrivals at JFK often take an hour or more to clear immigration and collect bags. Your free waiting time starts when your flight lands, and your chauffeur follows the flight, so you are not paying for a slow customs line.',
    ],
    facts: [
      ['To Midtown', 'about 17 miles'],
      ['Drive time', '40–60 min, up to 90 at rush hour'],
      ['Flat rate to Manhattan', `${money(jfk.sedan)} Sedan · ${money(jfk.suv)} SUV`],
      ['Free waiting', '60 min after landing'],
    ],
    priceTitle: <>JFK <em>prices</em></>,
    priceLead: 'Flat rates to and from Manhattan, and typical distance-based prices to Brooklyn and Queens.',
    priceTables: [{ caption: 'JFK car service prices', rows: jfkRows, firstColumn: 'Route' }],
    priceNote: 'Manhattan routes are flat rates. Brooklyn and Queens prices depend on distance; the examples are for Downtown Brooklyn and Long Island City.',
    sections: [
      {
        eyebrow: 'Meeting your chauffeur',
        title: <>Arriving <em>at JFK</em></>,
        paragraphs: [
          'JFK has several terminals linked by the AirTrain, and it is in the middle of a large rebuild, with new terminals opening and roads around the terminals changing. When you book, add your flight number: your chauffeur goes to the terminal your flight uses.',
          'After you land, you get a notification when your chauffeur has arrived. Once you have your bags, call or message them directly and they will tell you exactly where to meet. If immigration is slow, there is no need to hurry: waiting is free for the first 60 minutes after landing.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a RoolDrive car at night',
      },
      {
        eyebrow: 'Departures',
        title: <>Getting <em>to your flight</em></>,
        paragraphs: [
          'For a departure from JFK, allow at least 60 to 90 minutes for the drive from Manhattan at busy times, then the time your airline recommends for check-in and security, usually 3 hours before an international flight. Your chauffeur drops you at your terminal’s departures level.',
        ],
        list: ['Flat rate from anywhere in Manhattan', 'Sedan for up to 3, SUV for up to 6', 'Help with your bags at the curb'],
      },
    ],
    faqTitle: 'JFK car service questions',
    faq: [
      { q: 'How much is a car service from JFK to Manhattan?', a: `A flat ${money(jfk.sedan)} in a Sedan or ${money(jfk.suv)} in an SUV, to any address in Manhattan. It does not change with the time of day or traffic, and tolls are included.` },
      { q: 'How long does it take to get from JFK to Midtown?', a: 'About 40 to 60 minutes without heavy traffic, and up to 90 minutes on weekday afternoons and Friday evenings.' },
      { q: 'What if immigration takes a long time?', a: 'Waiting is free for the first 60 minutes after your flight lands, and your chauffeur tracks your flight. After that, waiting is charged per minute as set out in our Terms.' },
      { q: 'Which terminal will my chauffeur go to?', a: 'The terminal your flight uses. Add your flight number when you book, and your chauffeur will contact you when they arrive to confirm where to meet.' },
      route('jfk-brooklyn') && { q: 'How much is JFK to Brooklyn?', a: `About ${money(route('jfk-brooklyn').sedan)} in a Sedan to Downtown Brooklyn. Brooklyn rides are priced by distance, so your exact price is shown before you book.` },
      { q: 'Can I book a ride to JFK for an early flight?', a: 'Yes, at any time of day or night. Book ahead and you can change or cancel free of charge up to 1 hour before pickup.' },
    ].filter(Boolean),
    related: ['/laguardia-car-service', '/newark-airport-car-service', '/flat-rate-car-service', '/nyc-to-the-hamptons-car-service'],
  },

  lga: lgaRows.length && lga && {
    ...common('LGA'),
    path: '/laguardia-car-service',
    code: 'LGA',
    linkText: 'LaGuardia car service',
    eyebrow: 'LaGuardia car service',
    title: <>LaGuardia car service, <em>{money(lga.sedan)} to Manhattan</em></>,
    lead: `Private chauffeur between LaGuardia (LGA) and anywhere in Manhattan for a flat ${money(lga.sedan)} in a Sedan. Flight tracked, 60 minutes of free waiting, price upfront.`,
    heroImage: 'skyline-light-trails',
    from: fromOf(lgaRows),
    cta: 'Book your LaGuardia ride',
    introEyebrow: 'LaGuardia Airport',
    introTitle: <>The closest airport <em>to Manhattan</em></>,
    intro: [
      'LaGuardia sits on the north shore of Queens, about 9 miles from Midtown Manhattan. The usual route is the Grand Central Parkway to the RFK Bridge for the East Side and Upper Manhattan, or the Brooklyn-Queens Expressway and the Queens-Midtown Tunnel for Midtown and below.',
      'Without heavy traffic, the ride to Midtown takes 25 to 40 minutes. At rush hour, especially on weekday afternoons, it can take an hour. The flat rate to Manhattan stays the same either way.',
      'Most LaGuardia flights are domestic, so there is no immigration line and you are often out of the terminal soon after landing. Your chauffeur follows your flight, so they are there when you are.',
    ],
    facts: [
      ['To Midtown', 'about 9 miles'],
      ['Drive time', '25–40 min, up to 60 at rush hour'],
      ['Flat rate to Manhattan', `${money(lga.sedan)} Sedan · ${money(lga.suv)} SUV`],
      ['Free waiting', '60 min after landing'],
    ],
    priceTitle: <>LaGuardia <em>prices</em></>,
    priceLead: 'Flat rates to and from Manhattan, and typical distance-based prices to Brooklyn and Queens.',
    priceTables: [{ caption: 'LaGuardia car service prices', rows: lgaRows, firstColumn: 'Route' }],
    priceNote: 'Manhattan routes are flat rates. Brooklyn and Queens prices depend on distance; the examples are for Downtown Brooklyn and Long Island City.',
    sections: [
      {
        eyebrow: 'Meeting your chauffeur',
        title: <>Arriving <em>at LGA</em></>,
        paragraphs: [
          'LaGuardia has three terminals: A, B and C. Terminals B and C were completely rebuilt in recent years, with new roads and pickup areas. Add your flight number when you book and your chauffeur goes to the right terminal.',
          'You get a notification when your chauffeur has arrived. Once you have your bags, call or message them and they will tell you where to meet. Waiting is free for the first 60 minutes after landing.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a RoolDrive car at night',
      },
      {
        eyebrow: 'Departures',
        title: <>Getting <em>to your flight</em></>,
        paragraphs: [
          'For a departure from LaGuardia, allow 30 to 60 minutes for the drive from Midtown depending on the time of day, plus the time your airline recommends for check-in and security. Your chauffeur drops you at your terminal’s departures level.',
        ],
        list: ['Flat rate from anywhere in Manhattan', 'Short rides to Queens and Brooklyn', 'Help with your bags at the curb'],
      },
    ],
    faqTitle: 'LaGuardia car service questions',
    faq: [
      { q: 'How much is a car service from LaGuardia to Manhattan?', a: `A flat ${money(lga.sedan)} in a Sedan or ${money(lga.suv)} in an SUV, to any address in Manhattan. It does not change with the time of day or traffic, and tolls are included.` },
      { q: 'How long is the ride from LaGuardia to Midtown?', a: 'About 25 to 40 minutes without heavy traffic, and up to an hour at rush hour.' },
      { q: 'Which terminal will my chauffeur go to?', a: 'The terminal your flight uses: A, B or C. Add your flight number when you book and your chauffeur will confirm where to meet when they arrive.' },
      route('lga-queens') && { q: 'How much is LaGuardia to Long Island City or Astoria?', a: `About ${money(route('lga-queens').sedan)} in a Sedan to Long Island City. Queens rides are priced by distance, and your price is shown before you book.` },
      { q: 'Is a car service better than a taxi from LaGuardia?', a: 'You skip the taxi line, your chauffeur is waiting for your flight, and the price is set before you travel, with no meter running in traffic.' },
      { q: 'Can I change my pickup if my flight changes?', a: 'Yes, free of charge up to 1 hour before pickup, on the website or in the app. If your flight is only delayed, there is nothing to change: your chauffeur tracks it.' },
    ].filter(Boolean),
    related: ['/jfk-car-service', '/newark-airport-car-service', '/flat-rate-car-service', '/nyc-to-greenwich-ct-car-service'],
  },

  ewr: ewrRows.length && ewr && {
    ...common('EWR'),
    path: '/newark-airport-car-service',
    code: 'EWR',
    linkText: 'Newark airport car service',
    eyebrow: 'Newark airport car service',
    title: <>Newark airport car service, <em>{money(ewr.sedan)} to Manhattan</em></>,
    lead: `Private chauffeur from Newark Liberty (EWR) to anywhere in Manhattan for a flat ${money(ewr.sedan)} in a Sedan, and to Jersey City, Hoboken and Brooklyn. Flight tracked, price upfront.`,
    heroImage: 'williamsburg-bridge-dusk',
    from: fromOf(ewrRows),
    cta: 'Book your Newark ride',
    introEyebrow: 'Newark Liberty International',
    introTitle: <>Across the Hudson, <em>straight home</em></>,
    intro: [
      'Newark Liberty is in New Jersey, about 16 miles from Midtown Manhattan. The ride follows the New Jersey Turnpike and enters Manhattan through the Lincoln Tunnel for Midtown and the West Side, or the Holland Tunnel for SoHo, Tribeca and the Financial District.',
      'Without heavy traffic, the ride to Midtown takes 30 to 50 minutes. At rush hour, the tunnels can push that to 60 to 90 minutes. From Newark to any Manhattan address, the price is a flat rate, so tunnel traffic doesn’t change it.',
      'By public transport, getting from Newark to Manhattan means the AirTrain, a train to Penn Station and then a taxi or subway with your bags. A private car takes you from the terminal straight to your door.',
    ],
    facts: [
      ['To Midtown', 'about 16 miles'],
      ['Drive time', '30–50 min, up to 90 at rush hour'],
      ['Flat rate to Manhattan', `${money(ewr.sedan)} Sedan · ${money(ewr.suv)} SUV`],
      ['To Jersey City', 'about 20 min'],
    ],
    priceTitle: <>Newark <em>prices</em></>,
    priceLead: 'Flat rates to and from Manhattan, and typical distance-based prices to Brooklyn, Queens, Jersey City and Hoboken.',
    priceTables: [{ caption: 'Newark airport car service prices', rows: ewrRows, firstColumn: 'Route' }],
    priceNote: 'Manhattan routes are flat rates. The other prices depend on distance; the examples are for Downtown Brooklyn, Long Island City, Downtown Jersey City and Hoboken.',
    sections: [
      {
        eyebrow: 'Meeting your chauffeur',
        title: <>Arriving <em>at EWR</em></>,
        paragraphs: [
          'Newark has three terminals: A, B and C. Terminal A opened in a new building in 2023, and Terminal C is United’s hub. Add your flight number when you book so your chauffeur goes to the right one.',
          'You get a notification when your chauffeur has arrived. Once you have your bags, call or message them and they will tell you where to meet. For international arrivals, the first 60 minutes after landing are free, which covers most customs lines.',
        ],
        image: 'night-ride-rear-seat',
        imageAlt: 'Rear seat of a RoolDrive car at night',
      },
      {
        eyebrow: 'New Jersey and beyond',
        title: <>Not going <em>to Manhattan?</em></>,
        paragraphs: [
          'Newark is the closest major airport for Jersey City, Hoboken and much of northern New Jersey. Rides there, and to Brooklyn, Queens or Staten Island, are priced by distance and shown before you pay.',
        ],
        list: ['Jersey City and Hoboken', 'Brooklyn and Staten Island', 'Short Hills, Montclair and northern New Jersey', 'Return trips to Newark from Manhattan'],
      },
    ],
    faqTitle: 'Newark airport car service questions',
    faq: [
      { q: 'How much is a car service from Newark airport to Manhattan?', a: `A flat ${money(ewr.sedan)} in a Sedan or ${money(ewr.suv)} in an SUV, to any address in Manhattan.` },
      route('manhattan-ewr') && { q: 'How much is a car from Manhattan to Newark airport?', a: `From any Manhattan address to Newark, it is ${money(route('manhattan-ewr').sedan)} in a Sedan and ${money(route('manhattan-ewr').suv)} in an SUV.` },
      { q: 'How long is the ride from Newark to Midtown?', a: 'About 30 to 50 minutes without heavy traffic, and up to 90 minutes when the tunnels are busy.' },
      route('ewr-jersey-city') && route('ewr-hoboken') && { q: 'How much is Newark to Jersey City or Hoboken?', a: `About ${money(route('ewr-jersey-city').sedan)} to Downtown Jersey City and ${money(route('ewr-hoboken').sedan)} to Hoboken in a Sedan. These rides are priced by distance, shown before you book.` },
      { q: 'Which terminal will my chauffeur go to?', a: 'The terminal your flight uses: A, B or C. Add your flight number when you book, and your chauffeur will confirm where to meet when they arrive.' },
      { q: 'Is a car service easier than the train from Newark?', a: 'With luggage, usually yes: no AirTrain, no change at Penn Station and no taxi at the end. Your chauffeur meets you at the terminal and drops you at your door.' },
    ].filter(Boolean),
    related: ['/jfk-car-service', '/laguardia-car-service', '/flat-rate-car-service', '/nyc-to-new-jersey-car-service'],
  },
};

export const AIRPORT_PAGES = PAGES;
export const AIRPORT_ROUTES = Object.values(PAGES).filter(Boolean).map((p) => ({ ...p, fromPrice: cheapest(p.from) }));
